"use client";

import * as React from "react";
import { Funnel as Filter, Grid3x3, List, Eye, Heart, Star, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { AddToCartButton, EcommerceProductsProvider, ProductSortDropdown, ProductsFilter, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { WP_Query } from "@/integrations/wordpress/wp_query";
declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};
const current_wp_query_params = typeof wvcClient !== "undefined" ? wvcClient.getCurrentWPQueryParams() : {};
const wp_query = new WP_Query(current_wp_query_params);
function QuickViewModal({
  product
}: {
  product: any;
}) {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const images = [product.featured_image_url, ...(product.gallery_images || [])].filter(Boolean);
  const nextImage = () => setCurrentImageIndex(prev => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  return <DialogContent className="max-w-4xl p-0 overflow-hidden bg-alabaster border-platinum rounded-lg">
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="relative aspect-square bg-platinum group">
          {images.length > 0 ? <>
              <img src={images[currentImageIndex]} alt={`${product.title} - detail view`} className="h-full w-full object-cover transition-opacity duration-500" loading="lazy" />
              {images.length > 1 && <>
                  <Button variant="ghost" size="icon" onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-lg h-10 w-10 shadow-md">
                    <ChevronLeft className="size-5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-lg h-10 w-10 shadow-md">
                    <ChevronRight className="size-5" />
                  </Button>
                </>}
            </> : <div className="h-full w-full flex items-center justify-center text-graphite font-mono text-xs">
              NO IMAGE AVAILABLE
            </div>}
        </div>
        <div className="p-8 flex flex-col">
          <DialogHeader className="p-0 mb-6">
            <div className="font-mono text-[10px] tracking-widest text-graphite mb-2 uppercase">
              {product.categories?.[0]?.name || "COLLECTION"}
            </div>
            <DialogTitle className="heading text-3xl font-bold leading-tight">
              {product.title}
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-4 mb-6">
            <div className="text-2xl font-bold text-onyx body">
              ${product.price?.toFixed(2) || "0.00"}
            </div>
            {product.rating && <div className="flex items-center gap-1 border-l border-platinum pl-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`size-3 ${i < Math.floor(product.rating) ? "fill-accent text-accent" : "text-platinum"}`} />)}
                </div>
                <span className="font-mono text-[10px] text-graphite">({product.review_count || 0})</span>
              </div>}
          </div>

          {product.short_description && <div className="body text-sm text-graphite leading-relaxed mb-8 prose prose-sm" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}

          <div className="mt-auto space-y-4">
            <div className="flex items-center gap-4">
              <AddToCartButton productId={product.id} className="flex-1" buttonClassName={`w-full h-12 rounded-lg heading tracking-widest transition-all ${product.stock_status === "outofstock" ? "bg-platinum text-graphite cursor-not-allowed" : "bg-onyx text-alabaster hover:bg-accent"}`} text={product.stock_status === "outofstock" ? "OUT OF STOCK" : "ADD TO BAG"} showQuantity={true} />
            </div>
            <p className="font-mono text-[10px] text-center text-graphite tracking-widest">
              FREE SHIPPING ON ORDERS OVER $150
            </p>
          </div>
        </div>
      </div>
    </DialogContent>;
}
function ProductCard({
  product,
  index,
  spotlight = false
}: {
  product: any;
  index: number;
  spotlight?: boolean;
}) {
  const [isWishlisted, setIsWishlisted] = React.useState(false);
  const modelCode = `LSH-${String(product.id).padStart(4, "0")}`;
  const specs = [product.categories?.[0]?.name || "APPAREL", "PREMIUM", "EDITORIAL"];
  React.useEffect(() => {
    const wishlist = JSON.parse(localStorage.getItem("wishlist") || "[]");
    setIsWishlisted(wishlist.includes(product.id));
  }, [product.id]);
  const toggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const wishlist = JSON.parse(localStorage.getItem("wishlist") || "[]");
    let newWishlist;
    if (wishlist.includes(product.id)) {
      newWishlist = wishlist.filter((id: number) => id !== product.id);
      setIsWishlisted(false);
    } else {
      newWishlist = [...wishlist, product.id];
      setIsWishlisted(true);
    }
    localStorage.setItem("wishlist", JSON.stringify(newWishlist));
  };
  const isOutOfStock = product.stock_status === "outofstock";
  if (spotlight) {
    return <article data-index={index} className="col-span-full border border-iron bg-alabaster shadow-lg group relative overflow-hidden rounded-lg transition-all duration-500 hover:shadow-xl after:absolute after:bottom-0 after:left-0 after:h-1 after:w-0 after:bg-accent group-hover:after:w-full after:transition-all">
        <div className="absolute top-0 left-0 px-4 py-2 bg-onyx text-alabaster font-mono text-xs tracking-widest z-10 rounded-br-lg">
          § {String(index + 1).padStart(2, "0")} / SPOTLIGHT
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          <div className="relative group/img aspect-square overflow-hidden bg-platinum">
            <Link to={product.link || "#"} className="block h-full w-full">
              {product.featured_image_url ? <>
                  <img src={product.featured_image_url} alt={`${product.title} - ${product.categories?.[0]?.name || ''}`} className="h-full w-full object-cover transition-all duration-700 group-hover/img:scale-105 group-hover/img:opacity-0" loading="lazy" />
                  {product.gallery_images?.[0] && <img src={product.gallery_images[0]} alt={`${product.title} thumbnail`} className="absolute inset-0 h-full w-full object-cover opacity-0 transition-all duration-700 group-hover/img:opacity-100 group-hover/img:scale-105" loading="lazy" />}
                </> : <div className="h-full w-full bg-gradient-to-br from-platinum to-alabaster" />}
            </Link>
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="secondary" className="rounded-lg heading text-xs tracking-widest h-10 px-4">
                    <Eye className="size-4 mr-2" /> QUICK VIEW
                  </Button>
                </DialogTrigger>
                <QuickViewModal product={product} />
              </Dialog>
              <Button variant="secondary" size="icon" onClick={toggleWishlist} className={`rounded-lg h-10 w-10 transition-colors ${isWishlisted ? "text-accent" : "text-onyx"}`}>
                <Heart className={`size-4 ${isWishlisted ? "fill-accent" : ""}`} />
              </Button>
            </div>
          </div>
          <div className="p-8 lg:p-12 flex flex-col justify-between">
            <div>
              <div className="font-mono text-xs tracking-widest text-graphite mb-4">
                {modelCode} — SEASONAL ESSENTIAL
              </div>
              <Link to={product.link || "#"}>
                <h3 className="heading text-3xl lg:text-4xl font-bold mb-4 leading-tight hover:text-accent transition-colors">
                  {product.title}
                </h3>
              </Link>
              {product.short_description && <div className="body text-base text-graphite leading-relaxed mb-6 [&_p]:mb-2" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} />}
              <div className="grid grid-cols-3 gap-2 mb-8 border-t border-b border-platinum py-4">
                {specs.map((spec, i) => <div key={i} data-index={i} className="font-mono text-xs tracking-widest text-graphite">
                    {spec.toUpperCase()}
                  </div>)}
              </div>
            </div>
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="font-mono text-xs tracking-widest text-graphite mb-1">PRICE</div>
                <div className="text-3xl font-bold text-onyx body">
                  ${product.price?.toFixed(2) || "0.00"}
                </div>
              </div>
              <AddToCartButton productId={product.id} className="w-auto" buttonClassName={`rounded-lg hover:shadow-lg transition-all h-12 px-8 ${isOutOfStock ? "bg-platinum text-graphite cursor-not-allowed" : "bg-onyx text-alabaster"}`} text={isOutOfStock ? "OUT OF STOCK" : "ADD TO CART +"} showQuantity={false} />
            </div>
          </div>
        </div>
      </article>;
  }
  return <article data-index={index} className="group relative border border-platinum bg-alabaster hover:border-accent transition-all duration-300 rounded-lg overflow-hidden hover:shadow-xl after:absolute after:bottom-0 after:left-0 after:h-1 after:w-0 after:bg-accent group-hover:after:w-full after:transition-all">
      <div className="absolute top-3 left-3 z-10 font-mono text-xs tracking-widest text-graphite">
        § {String(index + 1).padStart(2, "0")}
      </div>
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-2 items-end">
        {isOutOfStock ? <Badge className="bg-graphite text-white border-none font-mono text-[10px] tracking-widest rounded-lg">
            SOLD OUT
          </Badge> : <Badge className="bg-accent text-white border-none font-mono text-[10px] tracking-widest rounded-lg">
            NEW
          </Badge>}
      </div>
      
      <div className="relative aspect-[3/4] overflow-hidden bg-platinum group/img">
        <Link to={product.link || "#"} className="block h-full w-full">
          {product.featured_image_url ? <>
              <img src={product.featured_image_url} alt={`${product.title} - ${product.categories?.[0]?.name || ''}`} className="h-full w-full object-cover transition-all duration-700 group-hover/img:scale-105 group-hover/img:opacity-0" loading="lazy" />
              {product.gallery_images?.[0] && <img src={product.gallery_images[0]} alt={`${product.title} thumbnail`} className="absolute inset-0 h-full w-full object-cover opacity-0 transition-all duration-700 group-hover/img:opacity-100 group-hover/img:scale-105" loading="lazy" />}
            </> : <div className="h-full w-full bg-platinum" />}
        </Link>
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="rounded-lg heading text-[10px] tracking-widest h-10 px-3">
                <Eye className="size-3 mr-1" /> QUICK VIEW
              </Button>
            </DialogTrigger>
            <QuickViewModal product={product} />
          </Dialog>
          <Button variant="secondary" size="icon" onClick={toggleWishlist} className={`rounded-lg h-10 w-10 transition-colors ${isWishlisted ? "text-accent" : "text-onyx"}`}>
            <Heart className={`size-3 ${isWishlisted ? "fill-accent" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="p-5 border-t border-platinum">
        <div className="flex justify-between items-start mb-2">
          <div className="font-mono text-[10px] tracking-widest text-graphite">
            {modelCode}
          </div>
          {product.rating && <div className="flex items-center gap-0.5">
              <Star className="size-2.5 fill-accent text-accent" />
              <span className="font-mono text-[9px] text-graphite">{product.rating}</span>
            </div>}
        </div>
        <Link to={product.link || "#"}>
          <h3 className="heading text-lg font-bold mb-3 leading-snug hover:text-accent transition-colors line-clamp-2 min-h-[3.5rem]">
            {product.title}
          </h3>
        </Link>
        <div className="grid grid-cols-3 gap-1 mb-4 pb-4 border-b border-platinum">
          {specs.map((spec, i) => <div key={i} data-index={i} className="font-mono text-[10px] tracking-widest text-graphite truncate">
              {spec.toUpperCase()}
            </div>)}
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-graphite">PRICE</div>
            <div className="text-xl font-bold text-onyx body">
              ${product.price?.toFixed(2) || "0.00"}
            </div>
          </div>
          <AddToCartButton productId={product.id} size="icon" className="w-auto" buttonClassName={`rounded-lg h-10 w-10 hover:shadow-md transition-all ${isOutOfStock ? "bg-platinum text-graphite cursor-not-allowed" : "bg-onyx text-alabaster"}`} text="" showQuantity={false} />
        </div>
      </div>
    </article>;
}
function ProductsGridConsumer({
  viewMode,
  setViewMode
}: {
  viewMode: "grid" | "list";
  setViewMode: (mode: "grid" | "list") => void;
}) {
  const {
    products,
    loading,
    error,
    refetch,
    total,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/4] border border-platinum bg-platinum animate-pulse rounded-lg" />)}
      </div>;
  }
  if (error) {
    return <div className="border border-platinum bg-alabaster p-12 text-center rounded-lg">
        <p className="font-mono text-sm tracking-widest text-graphite mb-4">
          CONNECTION ERROR
        </p>
        <Button onClick={refetch} variant="outline" className="font-mono tracking-widest border-onyx rounded-lg h-10 px-6">
          RETRY
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="border border-platinum bg-alabaster p-12 text-center rounded-lg">
        <p className="body text-lg text-graphite">
          No items found in this collection.
        </p>
      </div>;
  }
  const startIdx = (currentPage - 1) * products.length + 1;
  const endIdx = startIdx + products.length - 1;
  return <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-platinum">
        <div className="flex items-center gap-4">
          <div className="flex items-center border border-platinum rounded-lg overflow-hidden">
            <Button variant="ghost" size="icon" onClick={() => setViewMode("grid")} className={`h-10 w-10 rounded-lg ${viewMode === "grid" ? "bg-onyx text-alabaster" : "hover:bg-platinum"}`} aria-label="Grid view">
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setViewMode("list")} className={`h-10 w-10 rounded-lg ${viewMode === "list" ? "bg-onyx text-alabaster" : "hover:bg-platinum"}`} aria-label="List view">
              <List className="h-4 w-4" />
            </Button>
          </div>
          <div className="font-mono text-xs tracking-widest text-graphite">
            {String(startIdx).padStart(2, "0")} — {String(endIdx).padStart(2, "0")} OF{" "}
            {String(total).padStart(3, "0")}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {products.map((product, i) => {
        const isSpotlight = (i + 1) % 9 === 0;
        return <ProductCard key={product.id} product={product} index={i} spotlight={isSpotlight} />;
      })}
      </div>

      {totalPages > 1 && <div className="flex items-center justify-center gap-0 pt-8 border-t border-platinum">
          <Button variant="ghost" onClick={prevPage} disabled={!hasPrev} className="font-mono text-xs tracking-widest border-r border-platinum h-10 px-4 rounded-lg">
            ← PREV
          </Button>
          {[...Array(Math.min(totalPages, 7))].map((_, i) => {
        const pageNum = i + 1;
        const isActive = pageNum === currentPage;
        return <Button key={pageNum} data-index={i} variant="ghost" onClick={() => setPage(pageNum)} className={`font-mono text-xs tracking-widest rounded-lg border-r border-platinum h-10 w-10 p-0 ${isActive ? "bg-onyx text-alabaster" : "hover:bg-platinum"}`}>
                {String(pageNum).padStart(2, "0")}
              </Button>;
      })}
          <Button variant="ghost" onClick={nextPage} disabled={!hasNext} className="font-mono text-xs tracking-widest h-10 px-4 rounded-lg">
            NEXT →
          </Button>
        </div>}
    </div>;
}
export default function ShopContent() {
  const [viewMode, setViewMode] = React.useState<"grid" | "list">("grid");
  return <section data-section-id="2281" id="shop-content" className="bg-alabaster text-onyx py-24 px-6 lg:px-12">
      <div className="max-w-[1440px] mx-auto">
        <div className="mb-24 pb-12 border-b border-platinum">
          <div className="font-mono text-xs tracking-widest text-onyx mb-4">
            § 01 / CATALOG
          </div>
          <h1 className="heading text-6xl font-bold tracking-tighter mb-6">
            THE COLLECTION
          </h1>
          <p className="body text-xl text-graphite max-w-2xl">
            Explore our latest seasonal arrivals, meticulously crafted for the modern wardrobe.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="lg:hidden mb-6">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="w-full font-mono tracking-widest border-platinum bg-alabaster rounded-lg h-12">
                  <Filter className="h-4 w-4 mr-2" />
                  SHOW FILTERS
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[80vh] overflow-y-auto bg-alabaster border-t-2 border-accent rounded-t-xl">
                <SheetHeader className="border-b border-accent pb-4 mb-4">
                  <SheetTitle className="heading text-2xl tracking-tighter">
                    FILTERS
                  </SheetTitle>
                </SheetHeader>
                <div className="p-4 [&_label]:heading [&_label]:text-sm [&_h3]:border-b [&_h3]:border-accent [&_h3]:pb-2 [&_h3]:mb-4">
                  <ProductsFilter />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <aside className="hidden lg:block lg:col-span-3">
              <div className="sticky top-24 border border-platinum bg-alabaster p-8 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-accent">
                  <div className="font-mono text-xs tracking-widest text-onyx">
                    FILTERS
                  </div>
                  <Filter className="h-4 w-4 text-graphite" />
                </div>
                <div className="[&_label]:heading [&_label]:text-sm [&_h3]:border-b [&_h3]:border-accent [&_h3]:pb-2 [&_h3]:mb-4 [&_input[type=checkbox]]:accent-accent [&_input[type=range]]:accent-accent">
                  <ProductsFilter />
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-6">
              <div className="flex justify-end items-center">
                <span className="font-mono text-xs tracking-widest text-graphite mr-2 flex items-center">
                  SORT /
                </span>
                <ProductSortDropdown label="" className="rounded-lg heading text-xs border-platinum h-10 hover:border-accent transition-colors" />
              </div>

              <></>

              <ProductsGridConsumer viewMode={viewMode} setViewMode={setViewMode} />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}